import * as React from "react"
import { debounce } from "lodash"

import { TextSmall } from "@tenforce/product-fields"

import { GlobalState } from "../../../../store"
import { useAppDispatch, useAppSelector } from "../../../../hooks"

import { ITextField } from "../.."
import { updateField, setCurrentFieldInEditMode } from "../../slice"

import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { getAutoCompleteSuggestions } from "../../../../apis/autoComplete"
import { addErrorNotification } from "../../../notifications/actions"
import { Labels } from "../../../../util/Translations"

export interface ITextFieldProps {
    field: ITextField
    extraClasses?: string
    hideTitle?: boolean
    isParentLoading?: boolean
}

type Props = ITextFieldProps

export const TextSmallField = ({
    field,
    hideTitle,
    extraClasses,
    isParentLoading,
}: Props): JSX.Element => {
    const dispatch = useAppDispatch()

    const { isLoading: isFieldLoading, currentFieldInEditMode = "" } =
        useAppSelector((state: GlobalState) => state.fields)
    const { loadingResources } = useAppSelector(
        (state: GlobalState) => state.resources,
    )

    const {
        isAutoCompleteEnabled,
        position: _position,
        odataType: _odataType,
        value,
        itemId,
        maxLength,
        ...fieldProps
    } = field

    const {
        ClickHereToEdit,
        RequiredFieldErrorMessage,
        GetTextSuggestionError,
    } = Labels

    const [suggestions, setSuggestions] = React.useState<string[]>([])

    const setSuggestionsByValue = async (inputValue: string): Promise<void> => {
        setSuggestions(
            await getAutoCompleteSuggestions(
                itemId,
                field.fieldId,
                inputValue ?? "",
                undefined,
                undefined,
                GetTextSuggestionError,
            ),
        )
    }

    const getSuggestions = async (inputValue: string): Promise<void> => {
        try {
            await setSuggestionsByValue(inputValue)
        } catch (error) {
            dispatch(addErrorNotification(error.message))
        }
    }

    const handleGenerateSuggestions = React.useCallback(
        debounce(getSuggestions, 300),
        [getSuggestions, debounce],
    )

    // in case we are waiting for the field data to be loaded
    // or resources to be loaded for the internal links
    const isLoading =
        fieldProps.isLoading ||
        isParentLoading ||
        isFieldLoading ||
        loadingResources.length > 0

    const isDisabled =
        fieldProps.isDisabled ||
        !["", fieldProps.fieldId].includes(currentFieldInEditMode)

    const modifiedProps = hideTitle
        ? { ...fieldProps, title: "", description: "" }
        : fieldProps
    return (
        <TextSmall
            {...modifiedProps}
            suggestions={suggestions}
            onValueChanged={
                isAutoCompleteEnabled ? handleGenerateSuggestions : undefined
            }
            maxLength={maxLength}
            description={
                <HTMLAndInternalLinkParser
                    content={(field?.description as string) ?? ""}
                />
            }
            extraClasses={`${extraClasses} tw-w-full`}
            formatValue={(value: string): JSX.Element => {
                return (
                    <HTMLAndInternalLinkParser
                        content={value}
                        parseHTML={false}
                    />
                )
            }}
            isLoading={isLoading}
            mandatoryErrorText={RequiredFieldErrorMessage}
            onSave={async (newValue: string): Promise<void> => {
                if (newValue !== value) {
                    await dispatch(
                        updateField({
                            field,
                            newValue,
                        }),
                    ).unwrap()
                }
            }}
            placeholder={ClickHereToEdit}
            value={value}
            isDisabled={isDisabled}
            onFieldEnterEditMode={(fieldId): void => {
                dispatch(
                    setCurrentFieldInEditMode(fieldId ?? fieldProps.fieldId),
                )
            }}
            onFieldExitEditMode={(): void => {
                dispatch(setCurrentFieldInEditMode(""))
            }}
        />
    )
}

export default TextSmallField
